import styled from "styled-components";
import { PRIMARY_DARK_GREEN } from "../../constants/color";

type Props = {
  page: number;
  totalPage: number;
  onSetPage: (page: number) => void;
  onPrevious: () => void;
  onNext: () => void;
};

export const Pagination: React.FC<Props> = ({
  page,
  totalPage,
  onSetPage,
  onPrevious,
  onNext,
}) => {
  const _renderPageItem = (
    page: number,
    isCurrentPage: boolean,
    isEmpty?: boolean,
    wide?: boolean,
  ) => {
    return (
      <StyledPaginationButton
        key={`pagination-${page}`}
        onClick={() => {
          if (!isCurrentPage && !isEmpty && page > 0) onSetPage(page);
        }}
        $isCurrentPage={isCurrentPage}
        $isEmpty={isEmpty}
        $isWide={wide}
      >
        {!isEmpty ? page : "..."}
      </StyledPaginationButton>
    );
  };

  const _renderMultiplePageItems = () => {
    if (totalPage == 0) {
      return <>{_renderPageItem(0, false, true)}</>;
    }

    if (totalPage < 7) {
      return Array.from({ length: totalPage }, (x, i) => i).map((pg) =>
        _renderPageItem(pg + 1, page === pg + 1),
      );
    }

    if ([totalPage - 2, totalPage - 1, totalPage].includes(page)) {
      return (
        <>
          {_renderPageItem(1, false)}
          {_renderPageItem(0, false, true, true)}
          {_renderPageItem(totalPage - 3, false)}
          {_renderPageItem(totalPage - 2, page === totalPage - 2)}
          {_renderPageItem(totalPage - 1, page === totalPage - 1)}
          {_renderPageItem(totalPage, page === totalPage)}
        </>
      );
    }

    if ([1, 2, 3].includes(page)) {
      return (
        <>
          {_renderPageItem(1, page === 1)}
          {_renderPageItem(2, page === 2)}
          {_renderPageItem(3, page === 3)}
          {_renderPageItem(4, false)}
          {_renderPageItem(0, false, true, true)}
          {_renderPageItem(totalPage, false)}
        </>
      );
    }

    return (
      <>
        {_renderPageItem(1, false)}
        {_renderPageItem(0, false, true)}
        {_renderPageItem(page - 1, false)}
        {_renderPageItem(page, true)}
        {_renderPageItem(page + 1, false)}
        {_renderPageItem(0, false, true)}
        {_renderPageItem(totalPage, false)}
      </>
    );
  };

  return (
    <StyledPaginationContainer>
      <StyledPaginationButton
        onClick={onPrevious}
        $disabled={totalPage === 0 || page === 1}
        $isWide
      >
        前の画面
      </StyledPaginationButton>

      <StyledPaginationButtonContainer>
        {_renderMultiplePageItems()}
      </StyledPaginationButtonContainer>

      <StyledPaginationButton
        onClick={onNext}
        $disabled={totalPage === 0 || page === totalPage}
        $isWide
      >
        次の画面
      </StyledPaginationButton>
    </StyledPaginationContainer>
  );
};

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const StyledPaginationButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledPaginationButton = styled.div<{
  $disabled?: boolean;
  $isCurrentPage?: boolean;
  $isEmpty?: boolean;
  $isWide?: boolean;
}>`
  width: ${({ $isWide }) => ($isWide ? "60px" : "30px")};
  padding: 12px 0;
  border-top: 2px solid
    ${({ $isCurrentPage }) =>
      $isCurrentPage ? PRIMARY_DARK_GREEN : "transparent"};

  color: ${({ $isCurrentPage }) =>
    $isCurrentPage ? PRIMARY_DARK_GREEN : "#B3B3B3"};
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  text-align: center;
  cursor: ${({ $isEmpty, $disabled }) =>
    $isEmpty || $disabled ? "not-allowed" : "pointer"};
`;
